import type { AdminProfile, Permission } from '@artiman/shared';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { authApi, hasSessionHint, refreshSession } from '@/api/client';

export const useAuthStore = defineStore('auth', () => {
  const admin = ref<AdminProfile | null>(null);
  const initialized = ref(false);

  const permissions = computed(() => new Set(admin.value?.permissions ?? []));
  const can = (p?: Permission | Permission[]) =>
    !p || (Array.isArray(p) ? p : [p]).every(x => permissions.value.has(x));

  /** Restores the session from the refresh cookie (once per page load). */
  async function init() {
    if (initialized.value) return;
    if (hasSessionHint()) admin.value = (await refreshSession())?.subject ?? null;
    initialized.value = true;
  }

  function setAdmin(a: AdminProfile) {
    admin.value = a;
    initialized.value = true;
  }

  async function logout() {
    await authApi.logout().catch(() => undefined);
    admin.value = null;
  }

  /** Called when the API reports the session ended (revoked, disabled, expired). */
  function sessionLost() {
    admin.value = null;
  }

  return { admin, initialized, permissions, can, init, setAdmin, logout, sessionLost };
});
